<template>
  <div class="linked-tag-chips">
    <template v-for="chip in visibleChips">
      <button
        v-if="chip.clickable"
        :key="chip.key"
        type="button"
        class="linked-tag-chip linked-tag-chip--link"
        :style="{ '--chip-color': chip.color }"
        :title="chip.opensNewTab ? chip.tooltip : null"
        :aria-label="chip.ariaLabel"
        :disabled="disabled"
        @click.stop="$emit('open', chip.link)">
        <v-icon size="14" class="linked-tag-chip__icon">{{ chip.icon }}</v-icon>
        <span class="linked-tag-chip__name">{{ chip.label }}</span>
        <v-icon v-if="chip.opensNewTab" size="12" class="linked-tag-chip__new-tab">mdi-open-in-new</v-icon>
      </button>
      <span
        v-else
        :key="chip.key"
        class="linked-tag-chip"
        :style="{ '--chip-color': chip.color }"
        :aria-label="chip.ariaLabel">
        <v-icon size="14" class="linked-tag-chip__icon">{{ chip.icon }}</v-icon>
        <span class="linked-tag-chip__name">{{ chip.label }}</span>
      </span>
    </template>
    <button
      v-if="chipWindow.toggle"
      type="button"
      class="linked-tag-chips__toggle"
      :aria-expanded="expanded ? 'true' : 'false'"
      :aria-label="toggleAriaLabel"
      @click.stop="expanded = !expanded">
      {{ chipWindow.toggleLabel }}
    </button>
  </div>
</template>

<script>
  import { linkedTagChipWindow } from '../../modules/registerTableLayout';

  /**
   * One row's Linked Tags as tinted chips (showcase B). Every chip up to 5; above that the first 4
   * plus "+N · Show all M", expanding in place. The parent presents each chip (label, colour, icon,
   * whether it opens a new tab) and handles `open`, so navigation keeps a single code path.
   */
  export default {
    name: 'LinkedTagChips',
    props: {
      /** [{ key, label, color, icon, clickable, opensNewTab, tooltip, ariaLabel, link }] */
      chips: { type: Array, default: () => [] },
      rowLabel: { type: [String, Number], default: '' },
      disabled: { type: Boolean, default: false },
    },
    data() {
      return { expanded: false };
    },
    computed: {
      chipWindow() {
        return linkedTagChipWindow(this.chips.length, this.expanded);
      },
      visibleChips() {
        return this.chips.slice(0, this.chipWindow.visibleCount);
      },
      toggleAriaLabel() {
        const row = this.rowLabel !== '' && this.rowLabel !== null ? ` for row ${this.rowLabel}` : '';
        return this.expanded
          ? `Show fewer linked Tags${row}`
          : `Show all ${this.chips.length} linked Tags${row}`;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .linked-tag-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 4px 0;
  }

  .linked-tag-chip {
    --chip-color: #665b9b;

    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 100%;
    height: 24px;
    padding: 0 8px 0 6px;
    border: 1px solid color-mix(in srgb, var(--chip-color) 45%, transparent);
    border-radius: var(--ds-radius-full, 9999px);
    background: color-mix(in srgb, var(--chip-color) 11%, transparent);
    color: var(--v-text-base);
    font-family: inherit;
    font-size: var(--ds-font-caption, 12px);
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    line-height: 1;
    white-space: nowrap;
    cursor: default;

    &__icon.v-icon { color: var(--chip-color); }

    &__name {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__new-tab.v-icon {
      margin-left: 1px;
      color: var(--register-chip-muted, #59635f);
    }

    &--link {
      cursor: pointer;
      transition: background-color var(--ds-transition-fast, 0.15s ease);

      &:hover:not(:disabled) {
        background: color-mix(in srgb, var(--chip-color) 20%, transparent);
      }

      &:focus-visible {
        outline: var(--ds-focus-ring-width, 2px) solid var(--v-primary-base);
        outline-offset: 1px;
      }

      &:disabled { cursor: default; opacity: 0.6; }
    }
  }

  .linked-tag-chips__toggle {
    height: 24px;
    padding: 0 9px;
    border: 1px dashed var(--ds-border-default, rgba(0, 0, 0, 0.12));
    border-radius: var(--ds-radius-full, 9999px);
    background: transparent;
    color: var(--v-primary-base);
    font-family: inherit;
    font-size: var(--ds-font-caption, 12px);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    cursor: pointer;

    &:hover { background: var(--ds-surface-active, rgba(3, 98, 76, 0.08)); }

    &:focus-visible {
      outline: var(--ds-focus-ring-width, 2px) solid var(--v-primary-base);
      outline-offset: 1px;
    }
  }

  .theme--dark {
    .linked-tag-chip {
      --register-chip-muted: #c8c8c8;

      &__icon.v-icon { color: color-mix(in srgb, var(--chip-color) 65%, #fff); }
    }

    .linked-tag-chips__toggle { color: var(--v-secondary-base); }
  }

  @media (prefers-reduced-motion: reduce) {
    .linked-tag-chip--link { transition: none; }
  }
</style>
